// OrderPage.js
import React, { useState, useEffect, useMemo } from 'react'; 
import axios from 'axios';
import styles from './ChatApp.module.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getAddressArray = (addr) => {
    if (!addr) return ["Not Available"];
    return [
        addr.addressLine1,
        `${addr.city}, ${addr.state} ${addr.zipCode}`,
        addr.country || "USA"
    ];
};
const formatCurrency = (val) => `$${Number(val || 0).toFixed(2)}`;

const generatePdfDocument = (data, type) => {
    const doc = new jsPDF();
    
    const distributorName = data.distributorInfo?.company_name || data.distributorInfo?.name || "Distributor";
    const distributorAddressLines = [];

    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text("FROM:", 14, 20);
    doc.text(distributorName, 14, 26);
    doc.setFont(undefined, 'normal');

    if (data.distributorInfo?.address) {
        const addr = data.distributorInfo.address;
        distributorAddressLines.push(addr.addressLine1);
        distributorAddressLines.push(`${addr.city}, ${addr.state} ${addr.zipCode}`);
        
        if (data.distributorInfo.email) {
            distributorAddressLines.push(data.distributorInfo.email);
        }
        if (data.distributorInfo.phone) {
            distributorAddressLines.push(data.distributorInfo.phone);
        }
    } 
    
    doc.text(distributorAddressLines, 14, 32);
    const storeAddress = getAddressArray(
        type === 'invoice' ? data.billingAddress : data.shippingAddress
    );
    doc.setFont(undefined, 'bold');
    doc.text(type === 'invoice' ? "BILL TO:" : "SHIP TO:", 110, 20);
    doc.text(data.storeName, 110, 26);
    doc.setFont(undefined, 'normal');
    doc.text(storeAddress, 110, 32);

    doc.setLineWidth(0.5);
    doc.line(14, 55, 196, 55);

    const title = type === 'invoice' ? "INVOICE" : "PICK LIST";
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text(title, 14, 70);
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text(`Order ID: ${data.orderId}`, 14, 80);
    doc.text(`Order Date: ${new Date(data.createdAt).toLocaleDateString()}`, 14, 86);
    if (type === 'invoice') {
        doc.text(`Payment Terms: ${data.paymentTerms}`, 14, 92);
    }
    
    let tableColumn, tableRows;
    if (type === 'invoice') {
        tableColumn = ["SKU", "Product", "Qty", "Price", "Subtotal"];
        tableRows = data.items.map(item => [
            item.sku, item.name, item.quantity,
            formatCurrency(item.price), formatCurrency(item.subtotal)
        ]);
    } else {
        tableColumn = ["SKU", "Product", "Quantity", "[  ] Picked"];
        tableRows = data.items.map(item => [item.sku, item.name, item.quantity, ""]);
    }

    autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 100,
    });
    
    if (type === 'invoice') {
        const finalY = doc.lastAutoTable.finalY || 150; 
        doc.setFontSize(12);
        doc.text(`Subtotal: ${formatCurrency(data.subtotal)}`, 140, finalY + 10);
        doc.text(`Tax: ${formatCurrency(data.tax)}`, 140, finalY + 16);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Total: ${formatCurrency(data.total)}`, 140, finalY + 24);
    }
    
    doc.save(`${type}_${data.orderId}.pdf`);
};

const OrderPage = () => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [selectedOrders, setSelectedOrders] = useState(new Set());
    
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const orderStatuses = ["New", "Confirmed", "Delivered", "Cancelled", "Returned"];

    const fetchOrders = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/orders`);
            setOrders(response.data);
            setSelectedOrders(new Set()); 
        } catch (err) {
            setError('Failed to fetch orders.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);
    
    const filteredOrders = useMemo(() => {
        let filtered = [...orders];
        
        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(order =>
                order.orderId.toLowerCase().includes(lowerQuery) ||
                order.storeName.toLowerCase().includes(lowerQuery) ||
                order.salesRepName.toLowerCase().includes(lowerQuery) ||
                order.status.toLowerCase().includes(lowerQuery)
            );
        }
        
        return filtered;
    }, [orders, searchQuery]);
    
    const paginatedData = useMemo(() => {
        const totalItems = filteredOrders.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        
        const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));
        
        const startIndex = (validCurrentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        
        const itemsOnPage = filteredOrders.slice(startIndex, endIndex);
        
        return { itemsOnPage, totalPages, totalItems, startIndex };
    }, [filteredOrders, currentPage, itemsPerPage]);

    useEffect(() => {
        if (currentPage > paginatedData.totalPages && paginatedData.totalPages > 0) {
            setCurrentPage(paginatedData.totalPages);
        } else if (currentPage === 0 && paginatedData.totalPages > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, paginatedData.totalPages]);

    const handleStatusChange = async (orderId, newStatus) => {
        if (!orderId) {
            setError("Cannot update status: Order ID is missing.");
            return;
        }
        try {
            const response = await axios.put(`${API_BASE_URL}/orders/${orderId}`, { status: newStatus });
            setOrders(prevOrders => 
                prevOrders.map(order => 
                    order.orderId === orderId ? response.data : order
                )
            );
            setMessage(`Order ${orderId} updated to ${newStatus}.`);
        } catch (err) {
            setError(`Failed to update order ${orderId}.`);
        }
    };
    
    const handleGeneratePdf = async (order, type) => {
        if (!order || !order.orderId) {
            setError("Failed to generate PDF: invalid order data.");
            return;
        }
        const orderId = order.orderId;
        const endpoint = `/orders/${orderId}/${type}`;
        const filename = `${type}_${orderId}.pdf`;
        
        setMessage(`Generating ${filename}...`);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}${endpoint}`);
            generatePdfDocument(response.data, type);
            setMessage('');
        } catch (err) {
            console.error(err);
            setError(`Failed to generate ${filename}.`);
        }
    };

    const handleExportERP = async (endpoint, filename, dataType = 'csv') => {
        setMessage(`Generating ${filename}...`);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}${endpoint}`);
            let data, contentType;
            if (dataType === 'csv') {
                data = response.data.csv_data || response.data;
                contentType = 'text/csv;charset=utf-8;';
            } else {
                data = JSON.stringify(response.data, null, 2);
                contentType = 'application/json';
            }
            
            const blob = new Blob([data], { type: contentType });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            window.URL.revokeObjectURL(link.href);

            setMessage('');
        } catch (err) {
            setError(`Failed to generate ${filename}.`);
            console.warn("ERP Export Error:", err);
        }
    };

    const handleExportSelectedERP = async () => {
        const idsToExport = Array.from(selectedOrders);
        if (idsToExport.length === 0) {
            setError("Please select at least one order to export.");
            return;
        }

        const filename = `erp_export_selected.csv`;
        setMessage(`Generating export for ${idsToExport.length} orders...`);
        setError('');

        try {
            const response = await axios.post(
                `${API_BASE_URL}/orders/export/selected`,
                { order_ids: idsToExport }, 
                { responseType: 'blob' } 
            );
            
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            window.URL.revokeObjectURL(link.href);

            setMessage(''); 
        } catch (err) {
            console.error("Selected Export Error:", err);
            setError(`Failed to generate selected export.`);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); 
    };

    const handlePrevPage = () => {
        setCurrentPage(p => Math.max(1, p - 1));
    };
    const handleNextPage = () => {
        setCurrentPage(p => Math.min(paginatedData.totalPages, p + 1));
    };
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); 
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allFilteredOrderIds = new Set(filteredOrders.map(o => o.orderId));
            setSelectedOrders(allFilteredOrderIds);
        } else {
            setSelectedOrders(new Set());
        }
    };
    
    const handleSelectOne = (orderId) => {
        const newSelection = new Set(selectedOrders);
        if (newSelection.has(orderId)) {
            newSelection.delete(orderId);
        } else {
            newSelection.add(orderId);
        }
        setSelectedOrders(newSelection);
    };

    const isAllSelected = filteredOrders.length > 0 && selectedOrders.size === filteredOrders.length;

    return (
        <div className={styles.adminPageContainer}>
            <div className={styles.adminHeader}>
                <h2 className={styles.adminPageTitle}>Order Management</h2>
                <div>
                    <button 
                        onClick={handleExportSelectedERP}
                        className={styles.refreshButton} 
                        disabled={selectedOrders.size === 0}
                        style={{backgroundColor: 'var(--primary-color)', color: 'white', marginRight: '10px'}}
                    >
                        Export Selected ({selectedOrders.size})
                    </button>
                    <button 
                        onClick={() => handleExportERP('/orders/export/erp', 'erp_export_all.csv', 'csv')} 
                        className={styles.refreshButton} 
                        style={{backgroundColor: 'var(--accent-light)', color: 'var(--accent-hover)'}}
                    >
                        Export All to ERP
                    </button>
                    <button onClick={fetchOrders} disabled={isLoading} className={styles.refreshButton} style={{marginLeft: '10px'}}>
                        {isLoading ? 'Refreshing...' : 'Refresh Orders'}
                    </button>
                </div>
            </div>

            {message && <div className={styles.adminSuccess} style={{marginBottom: '15px'}}>{message}</div>}
            {error && <div className={styles.adminError} style={{marginBottom: '15px'}}>{error}</div>}

            <div className={styles.adminCard} style={{marginBottom: '1rem'}}>
                <input
                    type="text"
                    placeholder="Search by Order ID, Store, Rep, or Status..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className={styles.adminInput}
                    style={{width: '100%', boxSizing: 'border-box'}}
                />
            </div>

            <div className={styles.adminCard}>
                <h3 className={styles.adminSectionTitle}>All Orders</h3>
                <div className={styles.tableResponsive}>
                    <table className={styles.analyticsTable}>
                        <thead>
                            <tr>
                                <th style={{width: '20px'}}>
                                    <input 
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        disabled={filteredOrders.length === 0}
                                    />
                                </th>
                                <th>Order ID</th>
                                <th>Date</th>
                                <th>Store Name</th>
                                <th>Sales Rep</th>
                                <th>Total</th>
                                <th>Payment Terms</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && <tr><td colSpan="9">Loading orders...</td></tr>}
                            {!isLoading && orders.length > 0 && filteredOrders.length === 0 && (
                                <tr><td colSpan="9">No orders match your search.</td></tr>
                            )}
                            {!isLoading && orders.length === 0 && (
                                <tr><td colSpan="9">No orders found.</td></tr>
                            )}
                            
                            {paginatedData.itemsOnPage.map(order => (
                                <tr key={order.orderId} className={selectedOrders.has(order.orderId) ? styles.selectedRow : ''}>
                                    <td>
                                        <input 
                                            type="checkbox"
                                            checked={selectedOrders.has(order.orderId)}
                                            onChange={() => handleSelectOne(order.orderId)}
                                        />
                                    </td>
                                    <td data-label="Order ID">{order.orderId}</td>
                                    <td data-label="Date">{new Date(order.createdAt).toLocaleDateString()}</td>
                                    <td data-label="Store Name">{order.storeName}</td>
                                    <td data-label="Sales Rep">{order.salesRepName}</td>
                                    <td data-label="Total">{formatCurrency(order.total)}</td>
                                    <td data-label="Payment Terms">{order.paymentTerms}</td>
                                    <td data-label="Status">
                                        <select 
                                            value={order.status} 
                                            onChange={(e) => handleStatusChange(order.orderId, e.target.value)}
                                            className={styles.statusSelect}
                                        >
                                            {orderStatuses.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td data-label="Actions" className={styles.orderActions}>
                                        <button 
                                            className={styles.linkButton}
                                            onClick={() => handleGeneratePdf(order, 'invoice')}
                                        >
                                            Invoice
                                        </button>
                                        <button 
                                            className={styles.linkButton}
                                            onClick={() => handleGeneratePdf(order, 'picklist')}
                                        >
                                            Pick List
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!isLoading && paginatedData.totalItems > 0 && (
                    <div className={styles.paginationControls}>
                        <div className={styles.paginationInfo}>
                            <span>
                                Showing {Math.min(1 + paginatedData.startIndex, paginatedData.totalItems)}
                                - {Math.min(paginatedData.startIndex + itemsPerPage, paginatedData.totalItems)} of {paginatedData.totalItems}
                            </span>
                            <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>per page</span>
                        </div>
                        <div className={styles.paginationButtonNav}>
                            <button 
                                onClick={handlePrevPage} 
                                disabled={currentPage === 1}
                                className={styles.adminButton}
                            >
                                Previous
                            </button>
                            <span>Page {currentPage} of {paginatedData.totalPages || 1}</span>
                            <button 
                                onClick={handleNextPage} 
                                disabled={currentPage === paginatedData.totalPages || paginatedData.totalPages === 0}
                                className={styles.adminButton}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderPage;